import { useCollectionProducts } from '@/hooks/react-query/products/use-collection-products'
import { stringToSlug } from '@/lib/utils'
import { GroupedVariants, ProductFormatted, TGenderType } from '@/types/product'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { useCollectionStore } from '../store/collection-store'

const sortColorPriorityFirst = (
  data: GroupedVariants,
  priorityColor?: string
): GroupedVariants => {
  if (!priorityColor) return data

  const prioritySlug = stringToSlug(priorityColor)
  const entries = Object.entries(data).map(([key, value], index) => ({
    key,
    value,
    index,
    starts: stringToSlug(key).startsWith(prioritySlug),
  }))

  entries.sort((a, b) => {
    if (a.starts && !b.starts) return -1
    if (b.starts && !a.starts) return 1
    return a.index - b.index
  })

  return Object.fromEntries(entries.map(({ key, value }) => [key, value]))
}

interface useCollectionProps {
  slug: string
  isFlatten?: boolean
  genderType?: TGenderType
  filterDefault?: {
    [key: string]: string | number | boolean
  }[]
}

export const useCollection = ({
  slug,
  isFlatten,
  genderType,
  filterDefault,
}: useCollectionProps) => {
  const setProductCount = useCollectionStore((s) => s.setProductCount)
  const searchParams = useSearchParams()
  const filter = useCollectionStore((s) => s.filter)

  const filterParams = useMemo(() => {
    let params = { ...filter } as {
      [key: string]: string | string[] | number | undefined
    }

    if (filterDefault) {
      params = {
        ...params,
        ...Object.fromEntries(
          filterDefault.map((item) => [item.key, item.value])
        ),
      }
    }

    if (slug === 'san-pham-moi') {
      params = {
        ...params,
        sort: 'new',
      }
    }

    if (isFlatten) {
      params = {
        ...params,
        force_flatten: 1,
      }
    }
    return params
  }, [filter, isFlatten, filterDefault, slug])

  const normalizedFilterParams = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filterParams).map(([key, value]) => [
          key,
          Array.isArray(value) ? value.join(',') : value,
        ])
      ),
    [filterParams]
  )

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useCollectionProducts({
      slug,
      limit: 12,
      hide_banner: true,
      show_variant_color: false,
      random_focus: false,
      flatten: 0,
      gender_type: genderType,
      ...normalizedFilterParams,
    })

  useEffect(() => {
    if (data) {
      setProductCount(data?.pages[0]?.total || 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setProductCount])

  const colorConcat = data?.pages[0]?.request?.color || ''
  const colorSlug = searchParams?.get('color') || colorConcat || ''

  const allProducts = useMemo(
    () =>
      (
        data?.pages?.flatMap((page) =>
          page?.data?.map((item) => ({
            ...item,
            mapped_variants: sortColorPriorityFirst(
              item.mapped_variants,
              colorSlug
            ),
          }))
        ) ?? []
      ).filter((item): item is ProductFormatted => !!item),
    [colorSlug, data?.pages]
  )

  const totalProducts = data?.pages[0]?.total || 0
  const currentShowing = allProducts.length

  const handleLoadMore = async () => {
    await fetchNextPage()
  }
  console.log('🧙 ~ allProducts:', allProducts)
  return {
    allProducts,
    totalProducts,
    currentShowing,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    handleLoadMore,
  }
}
